'use client';

/**
 * Padel District — Official Brand Guidelines Implementation
 *
 * Brand Colors:
 *   Deep Blue   #00408F  primary background / base
 *   Bright Blue #015EFF  accent + ALL CTAs (replaced the lime green, which the
 *                        client found "too strong" — matches padeldistrict.com.sa)
 *   Light Blue  #ABC8D3  muted labels, secondary text
 *   Neutral Gray #F4F4F4 light section backgrounds
 *   Black       #000000  text on light backgrounds
 *
 *   NOTE: Fresh Green #B0FF06 is still an official brand color but is
 *         intentionally NOT used in this UI per client request.
 *
 * Typography:
 *   Barlow Condensed ExtraBold (Normalidad substitute) — headlines, uppercase
 *   DM Sans — body copy, labels, navigation
 *
 * NOTE: WhatsApp / contact number — +966 50 019 1234.
 */

import { useEffect, useRef, useState } from 'react';

import Link from '~/(tenant)/_components/tenant-link';

import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  Clock,
  MapPin,
  PartyPopper,
  Phone,
  Trophy,
  Users,
} from 'lucide-react';

import './styles.css';

const HERO_IMG = '/landing-pages/padeldistrict/images/hero-court.webp';
const ACADEMY_IMG = '/landing-pages/padeldistrict/images/academy-section.webp';
const LIFESTYLE_IMG =
  '/landing-pages/padeldistrict/images/lifestyle-section.webp';
const COMMUNITY_IMG =
  '/landing-pages/padeldistrict/images/community-section.webp';
const GABY_IMG = '/landing-pages/padeldistrict/images/gaby-reca-section.webp';
const WHATSAPP_HREF = 'https://wa.me/966500191234';
// Jeddah flagship — Al Mohammadiyyah (Google Maps pin).
const MAPS_HREF = 'https://maps.app.goo.gl/Mj8XkJn8mEk9E1kA6';
const BOOK_HREF = '/book';
const CONTACT_HREF = '/contact';

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.6s cubic-bezier(0.23,1,0.32,1) ${delay}ms, transform 0.6s cubic-bezier(0.23,1,0.32,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function WhatsAppFloat() {
  return (
    <a
      href={WHATSAPP_HREF}
      className="whatsapp-float"
      aria-label="WhatsApp"
      target="_blank"
      rel="noopener noreferrer"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
}

function StickyMobileBar() {
  return (
    <div
      className="sticky-mobile-bar md:hidden"
      style={{ backgroundColor: '#00408F' }}
    >
      <Link
        href={BOOK_HREF}
        className="flex-1 flex items-center justify-center gap-1.5 py-3.5"
        style={{
          backgroundColor: '#015EFF',
          color: '#fff',
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        Book Court
      </Link>
      <Link
        href={BOOK_HREF}
        className="flex-1 flex items-center justify-center gap-1.5 py-3.5"
        style={{
          backgroundColor: '#00408F',
          color: '#fff',
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        Coaching
      </Link>
      <a
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 flex items-center justify-center gap-1.5 py-3.5"
        style={{
          backgroundColor: '#003578',
          color: '#fff',
          fontFamily: "'DM Sans',sans-serif",
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        WhatsApp
      </a>
    </div>
  );
}

function HeroSection() {
  return (
    <section
      className="relative min-h-screen flex items-end"
      style={{ backgroundColor: '#00408F' }}
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Padel District Courts"
          className="w-full h-full object-cover"
          style={{ opacity: 0.55 }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, rgba(0,64,143,0.92) 0%, rgba(0,64,143,0.55) 55%, rgba(0,64,143,0.25) 100%)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, rgba(0,64,143,0.98) 0%, rgba(0,64,143,0.2) 40%, transparent 65%)',
          }}
        />
      </div>

      <div className="container relative z-10 pb-24 md:pb-32 pt-28">
        <div
          className="flex flex-wrap gap-4 mb-8"
          style={{
            opacity: 0,
            animation:
              'fadeUp 0.6s cubic-bezier(0.23,1,0.32,1) 0.15s forwards',
          }}
        >
          {['Gaby Reca Academy', 'Saudi Arabia', 'Wellness + Coffee'].map(
            (tag) => (
              <span
                key={tag}
                className="px-3 py-1 text-xs font-semibold uppercase tracking-widest"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: '#ABC8D3',
                  fontFamily: "'DM Sans',sans-serif",
                  letterSpacing: '0.12em',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
              >
                {tag}
              </span>
            ),
          )}
        </div>

        <h1
          className="text-white mb-5"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 'clamp(2.8rem, 8vw, 7.5rem)',
            fontWeight: 900,
            letterSpacing: '-0.02em',
            lineHeight: 0.95,
            textTransform: 'uppercase',
            maxWidth: '900px',
            opacity: 0,
            animation:
              'fadeUp 0.65s cubic-bezier(0.23,1,0.32,1) 0.28s forwards',
          }}
        >
          Saudi Arabia&apos;s
          <br />
          <span style={{ color: '#015EFF' }}>Premium Padel</span>
          <br />
          Destination.
        </h1>

        <p
          className="text-white/65 mb-10 max-w-lg leading-relaxed"
          style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: '1rem',
            opacity: 0,
            animation:
              'fadeUp 0.65s cubic-bezier(0.23,1,0.32,1) 0.42s forwards',
          }}
        >
          Book a court, join a social, or train with Gaby Reca Academy — built
          for play, training and community.
        </p>

        <div
          className="flex flex-wrap gap-3"
          style={{
            opacity: 0,
            animation:
              'fadeUp 0.65s cubic-bezier(0.23,1,0.32,1) 0.55s forwards',
          }}
        >
          <Link href={BOOK_HREF} className="btn-green">
            Book a Court <ArrowRight size={15} />
          </Link>
          <a href="#academy" className="btn-outline">
            Coaching
          </a>
          <a
            href={WHATSAPP_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline"
          >
            WhatsApp Help
          </a>
        </div>
      </div>
    </section>
  );
}

function Ticker() {
  const items = [
    'Book a Court',
    'Gaby Reca Academy',
    'Al Mohammadiyyah',
    'Wellness & Recovery',
    'Social Padel',
    'Ladies Sessions',
    'Kids Academy',
    'Corporate Events',
    'Brew92 Coffee',
  ];
  const doubled = [...items, ...items];
  return (
    <div
      className="overflow-hidden py-3.5"
      style={{ backgroundColor: '#015EFF' }}
    >
      <div
        className="ticker-track flex gap-0 whitespace-nowrap"
        style={{ width: 'max-content' }}
      >
        {doubled.map((item, i) => (
          <span
            key={`${item}-${i}`}
            className="flex items-center"
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: '0.85rem',
              fontWeight: 800,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#fff',
            }}
          >
            {item}
            <span className="mx-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
              ◆
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ActionCards() {
  const cards = [
    {
      Icon: CalendarDays,
      title: 'Book a Court',
      desc: 'Reserve one of 7 premium indoor courts. Available daily.',
      cta: 'Book Now',
      href: BOOK_HREF,
      accent: '#015EFF',
      external: false,
    },
    {
      Icon: Users,
      title: 'Join a Social',
      desc: 'Weekly socials, ladies sessions, and open play for all levels.',
      cta: 'See Schedule',
      href: '#events',
      accent: '#015EFF',
      external: false,
    },
    {
      Icon: Trophy,
      title: 'Book Coaching',
      desc: 'Train with Gaby Reca Academy — globally recognized.',
      cta: 'Book Session',
      href: BOOK_HREF,
      accent: '#FFFFFF',
      external: false,
    },
    {
      Icon: PartyPopper,
      title: 'Plan an Event',
      desc: 'Corporate tournaments, private events and group bookings.',
      cta: 'Enquire',
      href: WHATSAPP_HREF,
      accent: '#ABC8D3',
      external: true,
    },
  ];

  return (
    <section style={{ backgroundColor: '#003578' }}>
      <div className="container py-16 md:py-20">
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
        >
          {cards.map((card, i) => (
            <Reveal key={card.title} delay={i * 70}>
              <div
                className="card-hover p-8 flex flex-col gap-5 h-full"
                style={{ backgroundColor: '#003578' }}
              >
                <div
                  style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    borderRadius: '0',
                  }}
                >
                  <card.Icon size={18} style={{ color: card.accent }} />
                </div>
                <div>
                  <h3
                    className="mb-2"
                    style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      letterSpacing: '-0.01em',
                      textTransform: 'uppercase',
                      color: '#fff',
                    }}
                  >
                    {card.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{
                      color: '#ABC8D3',
                      fontFamily: "'DM Sans',sans-serif",
                    }}
                  >
                    {card.desc}
                  </p>
                </div>
                {card.external ? (
                  <a
                    href={card.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-colors"
                    style={{
                      fontFamily: "'DM Sans',sans-serif",
                      color: card.accent,
                      letterSpacing: '0.12em',
                    }}
                  >
                    {card.cta} <ChevronRight size={13} />
                  </a>
                ) : card.href.startsWith('#') ? (
                  <a
                    href={card.href}
                    className="mt-auto inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-colors"
                    style={{
                      fontFamily: "'DM Sans',sans-serif",
                      color: card.accent,
                      letterSpacing: '0.12em',
                    }}
                  >
                    {card.cta} <ChevronRight size={13} />
                  </a>
                ) : (
                  <Link
                    href={card.href}
                    className="mt-auto inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-colors"
                    style={{
                      fontFamily: "'DM Sans',sans-serif",
                      color: card.accent,
                      letterSpacing: '0.12em',
                    }}
                  >
                    {card.cta} <ChevronRight size={13} />
                  </Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  // Two locations. Jeddah (flagship) shows the real interior court photo.
  // Riyadh is unannounced — per client, NO photo: a "Coming Soon" panel.
  const locations = [
    {
      city: 'Jeddah',
      status: 'Now Open',
      desc: 'Our flagship in Al Mohammadiyyah — 7 premium indoor courts, the Gaby Reca Academy, wellness and social spaces all under one roof.',
      img: ACADEMY_IMG,
    },
    {
      city: 'Riyadh',
      status: 'Coming Soon',
      desc: 'The next Padel District — the same premium courts, world-class coaching and community experience, arriving soon in the capital.',
      img: null,
    },
  ];

  return (
    <section style={{ backgroundColor: '#00408F' }}>
      <div className="container py-20 md:py-28">
        <Reveal>
          <div className="flex items-center gap-3 mb-3">
            <div
              style={{
                width: '2.5rem',
                height: '2px',
                backgroundColor: '#015EFF',
              }}
            />
            <span className="section-label">Why Padel District</span>
          </div>
          <h2
            className="text-white mb-14"
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: 'clamp(2rem, 5vw, 4rem)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              maxWidth: '700px',
            }}
          >
            Two Cities.
            <br />
            <span style={{ color: '#015EFF' }}>One Community.</span>
          </h2>
        </Reveal>

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-px"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
        >
          {locations.map((loc, i) => (
            <Reveal key={loc.city} delay={i * 80}>
              <div
                className="card-hover group overflow-hidden h-full"
                style={{ backgroundColor: '#003578' }}
              >
                <div className="relative h-60 overflow-hidden">
                  {loc.img ? (
                    <img
                      src={loc.img}
                      alt={`Padel District ${loc.city}`}
                      className="w-full h-full object-cover transition-transform duration-600 group-hover:scale-103"
                      style={{
                        transition:
                          'transform 0.6s cubic-bezier(0.23,1,0.32,1)',
                      }}
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{
                        background:
                          'linear-gradient(135deg, #00408F 0%, #003578 100%)',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'Barlow Condensed',sans-serif",
                          fontSize: 'clamp(1.6rem, 4vw, 2.6rem)',
                          fontWeight: 900,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: 'rgba(255,255,255,0.16)',
                          textAlign: 'center',
                          lineHeight: 1,
                        }}
                      >
                        Coming
                        <br />
                        Soon
                      </span>
                    </div>
                  )}
                  <div className="img-overlay-bottom" />
                  <span
                    className="absolute top-4 left-5 px-2.5 py-1 text-xs font-bold uppercase"
                    style={{
                      backgroundColor: '#015EFF',
                      color: '#fff',
                      fontFamily: "'DM Sans',sans-serif",
                      fontSize: '0.6rem',
                      letterSpacing: '0.14em',
                    }}
                  >
                    {loc.status}
                  </span>
                </div>
                <div className="p-7">
                  <h3
                    className="text-white mb-3"
                    style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontSize: '1.75rem',
                      fontWeight: 900,
                      letterSpacing: '-0.01em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {loc.city}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{
                      color: '#ABC8D3',
                      fontFamily: "'DM Sans',sans-serif",
                    }}
                  >
                    {loc.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function EventsSection() {
  const events = [
    {
      day: 'Mon / Wed / Fri',
      title: 'Social Padel',
      desc: 'Open social sessions for all levels. Meet new players, enjoy the game.',
      tag: 'All Levels',
      Icon: Users,
    },
    {
      day: 'Tuesday',
      title: 'Ladies Social',
      desc: 'Dedicated ladies-only sessions in a welcoming, energetic environment.',
      tag: 'Ladies',
      Icon: Users,
    },
    {
      day: 'Saturday',
      title: 'Kids Academy',
      desc: 'Junior development program with structured coaching from certified trainers.',
      tag: 'Kids',
      Icon: Trophy,
    },
    {
      day: 'Flexible',
      title: 'Corporate Events',
      desc: 'Team-building tournaments and private court bookings for companies.',
      tag: 'Corporate',
      Icon: PartyPopper,
    },
  ];

  return (
    <section id="events" style={{ backgroundColor: '#003578' }}>
      <div className="container py-20 md:py-28">
        <Reveal>
          <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div
                  style={{
                    width: '2.5rem',
                    height: '2px',
                    backgroundColor: '#015EFF',
                  }}
                />
                <span className="section-label">This Week</span>
              </div>
              <h2
                className="text-white"
                style={{
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: 'clamp(1.8rem, 4vw, 3.5rem)',
                  fontWeight: 900,
                  letterSpacing: '-0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Socials & Events.
              </h2>
              <p
                className="mt-3"
                style={{
                  color: '#ABC8D3',
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: '0.95rem',
                }}
              >
                Running across both locations — Jeddah &amp; Riyadh.
              </p>
            </div>
            <Link href={BOOK_HREF} className="btn-green">
              Join a Social <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
        >
          {events.map((ev, i) => (
            <Reveal key={ev.title} delay={i * 70}>
              <div
                className="card-hover p-7 flex flex-col gap-4 h-full"
                style={{ backgroundColor: '#00408F' }}
              >
                <div className="flex items-center justify-between">
                  <span className="section-label" style={{ color: '#ABC8D3' }}>
                    {ev.day}
                  </span>
                  <span
                    className="px-2 py-0.5 text-xs font-bold uppercase tracking-wider"
                    style={{
                      backgroundColor: '#015EFF',
                      color: '#fff',
                      fontFamily: "'DM Sans',sans-serif",
                      fontSize: '0.6rem',
                      letterSpacing: '0.12em',
                    }}
                  >
                    {ev.tag}
                  </span>
                </div>
                <h4
                  className="text-white"
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: '1.35rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {ev.title}
                </h4>
                <p
                  className="text-sm leading-relaxed flex-1"
                  style={{
                    color: '#ABC8D3',
                    fontFamily: "'DM Sans',sans-serif",
                  }}
                >
                  {ev.desc}
                </p>
                <Link
                  href={BOOK_HREF}
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest"
                  style={{
                    color: '#015EFF',
                    fontFamily: "'DM Sans',sans-serif",
                    letterSpacing: '0.12em',
                  }}
                >
                  Register <ChevronRight size={12} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function AcademySection() {
  return (
    <section id="academy" style={{ backgroundColor: '#00408F' }}>
      <div className="container py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal className="relative">
            <div
              className="relative overflow-hidden"
              style={{ aspectRatio: '4/3' }}
            >
              <img
                src={GABY_IMG}
                alt="Gaby Reca Academy"
                className="w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to top, rgba(0,64,143,0.7) 0%, transparent 60%)',
                }}
              />
              <div
                className="absolute bottom-0 left-0 p-5"
                style={{ backgroundColor: '#015EFF' }}
              >
                <div
                  style={{
                    fontFamily: "'DM Sans',sans-serif",
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.18em',
                    color: '#fff',
                    textTransform: 'uppercase',
                    marginBottom: '2px',
                  }}
                >
                  Gaby Reca Academy
                </div>
                <div
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: '1.5rem',
                    fontWeight: 900,
                    color: '#fff',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                  }}
                >
                  Globally Recognized
                </div>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <div
                  style={{
                    width: '2.5rem',
                    height: '2px',
                    backgroundColor: '#015EFF',
                  }}
                />
                <span className="section-label">Gaby Reca Padel Academy</span>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <h2
                className="text-white mb-5"
                style={{
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                  fontWeight: 900,
                  letterSpacing: '-0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Train With
                <br />
                <span style={{ color: '#015EFF' }}>The World&apos;s Best.</span>
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p
                className="mb-5 leading-relaxed"
                style={{
                  color: '#ABC8D3',
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: '0.95rem',
                }}
              >
                Gaby Reca Academy is among the best padel academies in
                the world. Led by legendary player Gaby Reca — coach to Fede
                Chingotto, Paquito Navarro, Gemma Triay and Juan Lebrón — the
                Academy brings world-class methodology to Saudi Arabia.
              </p>
              <p
                className="mb-8 leading-relaxed"
                style={{
                  color: '#ABC8D3',
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: '0.95rem',
                }}
              >
                Structured programs for all levels: beginners, intermediate,
                advanced and junior development.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mb-9 space-y-2.5">
                {[
                  'Beginner through competitive player programs',
                  'Private sessions, group clinics & junior development',
                  'Level assessment and tailored training plans',
                  'Access to professional-grade indoor facilities',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div
                      className="mt-2 w-3 h-0.5 flex-shrink-0"
                      style={{ backgroundColor: '#015EFF' }}
                    />
                    <span
                      style={{
                        color: '#ABC8D3',
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: '0.9rem',
                      }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="flex flex-wrap gap-3">
                <Link href={BOOK_HREF} className="btn-green">
                  Book a Session <ArrowRight size={14} />
                </Link>
                <a
                  href={WHATSAPP_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                >
                  Coaching Enquiry
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function PartnersSection() {
  const partners = [
    {
      name: 'Brew92',
      category: 'Coffee & Social',
      desc: 'Specialty coffee and social space — the perfect pre and post-match ritual.',
      color: '#015EFF',
    },
    {
      name: 'Sweat Army',
      category: 'Ladies Gym',
      desc: 'A dedicated ladies-only gym — strength, conditioning and community in one space.',
      color: '#FFFFFF',
    },
    {
      name: 'Fitness Empire',
      category: 'Boutique Gym',
      desc: 'A boutique training studio for focused, high-quality workouts between matches.',
      color: '#015EFF',
    },
    {
      name: 'IMC Recovery',
      category: 'Physiotherapy',
      desc: 'Professional physiotherapy and sports recovery — keep your body match-ready.',
      color: '#ABC8D3',
    },
    {
      name: 'Bullpadel',
      category: 'Padel Gear & Retail',
      desc: 'Premium rackets, footwear and apparel from one of padel’s leading brands.',
      color: '#FFFFFF',
    },
  ];

  return (
    <section id="partners" style={{ backgroundColor: '#003578' }}>
      <div className="container py-20 md:py-28">
        <Reveal>
          <div className="flex items-center gap-3 mb-3">
            <div
              style={{
                width: '2.5rem',
                height: '2px',
                backgroundColor: '#015EFF',
              }}
            />
            <span className="section-label">Lifestyle Partners</span>
          </div>
          <h2
            className="text-white mb-5"
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: 'clamp(1.8rem, 4vw, 3.5rem)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
            }}
          >
            Recover, Refuel
            <br />
            <span style={{ color: '#015EFF' }}>& Connect.</span>
          </h2>
          <p
            className="mb-14 max-w-xl"
            style={{
              color: '#ABC8D3',
              fontFamily: "'DM Sans',sans-serif",
              fontSize: '0.95rem',
            }}
          >
            Every partner at Padel District is handpicked to complete your
            experience — before, during and after every match.
          </p>
        </Reveal>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
        >
          {partners.map((p, i) => (
            <Reveal key={p.name} delay={i * 60}>
              <div
                className="card-hover p-8 flex flex-col gap-3"
                style={{ backgroundColor: '#00408F' }}
              >
                <span className="section-label" style={{ color: p.color }}>
                  {p.category}
                </span>
                <h4
                  className="text-white"
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: '1.75rem',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {p.name}
                </h4>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: '#ABC8D3',
                    fontFamily: "'DM Sans',sans-serif",
                  }}
                >
                  {p.desc}
                </p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={300}>
            <div
              className="relative overflow-hidden sm:col-span-2 lg:col-span-1"
              style={{ minHeight: '220px' }}
            >
              <img
                src={COMMUNITY_IMG}
                alt="Club Lifestyle"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to top, rgba(0,53,120,0.9) 0%, rgba(0,53,120,0.3) 60%, transparent 100%)',
                }}
              />
              <div className="absolute bottom-0 left-0 p-7">
                <p
                  className="text-white"
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: '1.5rem',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.1,
                  }}
                >
                  More Than
                  <br />a Court.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function MembershipSection() {
  return (
    <section style={{ backgroundColor: '#015EFF' }}>
      <div className="container py-16 md:py-20">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Reveal>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div
                  style={{
                    width: '2.5rem',
                    height: '2px',
                    backgroundColor: '#fff',
                  }}
                />
                <span
                  className="section-label"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  Membership
                </span>
              </div>
              <h2
                className="text-white mb-3"
                style={{
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: 'clamp(1.6rem, 3vw, 2.8rem)',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: '-0.02em',
                }}
              >
                Priority Booking.
                <br />
                Community. Perks.
              </h2>
              <p
                style={{
                  color: 'rgba(255,255,255,0.7)',
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: '0.95rem',
                  maxWidth: '480px',
                }}
              >
                Members get priority court access, discounted coaching, partner
                benefits, exclusive events and academy perks — all in one
                membership.
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex flex-wrap gap-3">
              <a
                href={WHATSAPP_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline"
                style={{ borderColor: 'rgba(255,255,255,0.4)' }}
              >
                Ask via WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function LocationSection() {
  return (
    <section id="location" style={{ backgroundColor: '#00408F' }}>
      <div className="container py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <Reveal>
              <div className="flex items-center gap-3 mb-3">
                <div
                  style={{
                    width: '2.5rem',
                    height: '2px',
                    backgroundColor: '#015EFF',
                  }}
                />
                <span className="section-label">Find Us</span>
              </div>
              <h2
                className="text-white mb-8"
                style={{
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: 'clamp(1.8rem, 4vw, 3.5rem)',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: '-0.02em',
                }}
              >
                Location &
                <br />
                <span style={{ color: '#015EFF' }}>Opening Hours.</span>
              </h2>
            </Reveal>

            <Reveal delay={80}>
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <MapPin
                    size={18}
                    style={{
                      color: '#015EFF',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontFamily: "'DM Sans',sans-serif",
                        fontWeight: 600,
                        color: '#fff',
                        fontSize: '0.9rem',
                        marginBottom: '4px',
                      }}
                    >
                      Jeddah — Open Now
                    </div>
                    <div
                      style={{
                        color: '#ABC8D3',
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: '0.9rem',
                      }}
                    >
                      Al Mohammadiyyah, Jeddah
                    </div>
                    <div
                      style={{
                        marginTop: '12px',
                        fontFamily: "'DM Sans',sans-serif",
                        fontWeight: 600,
                        color: '#fff',
                        fontSize: '0.9rem',
                        marginBottom: '4px',
                      }}
                    >
                      Riyadh —{' '}
                      <span style={{ color: '#015EFF' }}>Coming Soon</span>
                    </div>
                    <div
                      style={{
                        color: '#ABC8D3',
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: '0.9rem',
                      }}
                    >
                      Location to be announced
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock
                    size={18}
                    style={{
                      color: '#015EFF',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontFamily: "'DM Sans',sans-serif",
                        fontWeight: 600,
                        color: '#fff',
                        fontSize: '0.9rem',
                        marginBottom: '2px',
                      }}
                    >
                      Opening Hours
                    </div>
                    <div
                      style={{
                        color: '#ABC8D3',
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: '0.9rem',
                      }}
                    >
                      Daily: 7:00 AM – 12:00 AM
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone
                    size={18}
                    style={{
                      color: '#015EFF',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontFamily: "'DM Sans',sans-serif",
                        fontWeight: 600,
                        color: '#fff',
                        fontSize: '0.9rem',
                        marginBottom: '2px',
                      }}
                    >
                      WhatsApp
                    </div>
                    <a
                      href={WHATSAPP_HREF}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#ABC8D3',
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: '0.9rem',
                      }}
                    >
                      Chat with us on WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={160}>
              <div className="flex flex-wrap gap-3">
                <Link href={BOOK_HREF} className="btn-green">
                  Book Before You Arrive <ArrowRight size={14} />
                </Link>
                <a
                  href={MAPS_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                >
                  Get Directions
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={100}>
            <div
              className="relative overflow-hidden"
              style={{ aspectRatio: '4/3', backgroundColor: '#003578' }}
            >
              <img
                src={LIFESTYLE_IMG}
                alt="Padel District Location"
                className="w-full h-full object-cover opacity-60"
              />
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-4"
                style={{ background: 'rgba(0,53,120,0.5)' }}
              >
                <div
                  className="px-6 py-4 text-center"
                  style={{ backgroundColor: '#015EFF' }}
                >
                  <div
                    style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      color: '#fff',
                      marginBottom: '4px',
                    }}
                  >
                    Now Open · Riyadh Coming Soon
                  </div>
                  <div
                    style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontSize: '1.25rem',
                      fontWeight: 900,
                      textTransform: 'uppercase',
                      color: '#fff',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Saudi Arabia
                  </div>
                </div>
                <a
                  href={MAPS_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                  style={{ borderColor: 'rgba(255,255,255,0.5)' }}
                >
                  Open in Maps
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: '#003578' }}
    >
      <img
        src={COMMUNITY_IMG}
        alt="Padel District Community"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: 0.3 }}
      />
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0,53,120,0.7)' }}
      />
      <div className="relative z-10 container py-24 md:py-32 text-center">
        <Reveal>
          <div className="flex items-center justify-center gap-3 mb-5">
            <div
              style={{
                width: '2.5rem',
                height: '2px',
                backgroundColor: '#015EFF',
              }}
            />
            <span className="section-label">Ready to Play?</span>
            <div
              style={{
                width: '2.5rem',
                height: '2px',
                backgroundColor: '#015EFF',
              }}
            />
          </div>
          <h2
            className="text-white mb-5 mx-auto"
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: 'clamp(2.5rem, 7vw, 6rem)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              maxWidth: '800px',
              lineHeight: 0.95,
            }}
          >
            Your Next Match
            <br />
            <span style={{ color: '#015EFF' }}>Starts Here.</span>
          </h2>
          <p
            className="mb-10 mx-auto"
            style={{
              color: '#ABC8D3',
              fontFamily: "'DM Sans',sans-serif",
              fontSize: '1rem',
              maxWidth: '440px',
            }}
          >
            Book a court, join a social, or train with Gaby Reca Academy. Saudi
            Arabia&apos;s premium padel destination is waiting.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <div className="flex flex-wrap justify-center gap-3">
            <Link href={BOOK_HREF} className="btn-green">
              Book a Court Now <ArrowRight size={14} />
            </Link>
            <Link href={CONTACT_HREF} className="btn-outline">
              Become a Partner
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function PadeldistrictLandingPage() {
  // Header + Footer are rendered by the shared shell (see
  // padeldistrict/components/{header,footer}.tsx, registered in
  // landing-page-layout-registry) so every sub-route gets them too. They were
  // removed from here to avoid rendering the chrome twice on `/`.
  return (
    <div className="padeldistrict-root min-h-screen">
      <WhatsAppFloat />
      <StickyMobileBar />
      <HeroSection />
      <Ticker />
      <ActionCards />
      <WhySection />
      <EventsSection />
      <AcademySection />
      <PartnersSection />
      <MembershipSection />
      <LocationSection />
      <FinalCTA />
    </div>
  );
}
